'use client';

import { LANDING_DESCRIPTION, LANDING_TITLE } from '@/utils/messages';
import {
  clearLastAuthEmailFromUrl,
  getActionFromUrl,
  getLastAuthEmailFromUrl,
  getMagicLinkQrUrl,
  getTrackIdFromStorage,
  getTrackIdFromUrl,
  saveTrackIdToStorage,
} from '@/utils/utils';
import { default as SuccessIcon } from '@mui/icons-material/CheckCircleRounded';
import {
  buildRecoveryUrl,
  PasswordlessLogin,
  type RecoveryReason,
} from '@verifime/cognito-passwordless-auth';
import {
  buildDesktopCameraGroup,
  buildIdentityRequirementGroups,
  DesktopQrReveal,
  IdentityRequirementSummary,
  Landing,
  LINK_REPLACEMENT_WARNING,
  LoginAlert,
  RESEND_NOW_MESSAGE,
} from '@verifime/components';
import { useFeatureToggle } from '@verifime/utils';
import { ClientLogoByTrackId, useWorkflowMetadataByTrackingReference } from '@verifime/views';
import { ClarityLogo } from '@/components/ClarityLogo';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { INITIALISE_SESSION } from './initialise-session';

export default function LoginPage() {
  const router = useRouter();
  const { isInertMagicLinkEnabled } = useFeatureToggle();

  // Next's Pages Router useRouter() returns a brand new object on every
  // call (it deliberately keeps query non-stateful), so router and
  // router.query cannot be trusted as useCallback deps: they are unstable
  // on every render, which would defeat the memoisation below. Holding the
  // latest router in a ref and reading through it at call time gives
  // signInRedirect and expiredLinkRedirect a genuinely empty dep array
  // while still always seeing the current router.
  const routerRef = useRef(router);
  routerRef.current = router;

  // The same unspent link this page was loaded with, in /token/<jwt> path form: the desktop QR
  // reveal's payload (undefined server-side, and whenever the page has no magic-link fragment).
  const linkUrl = getMagicLinkQrUrl();

  // Resolved on mount for the magic-link continue screen's attribution slot
  // (ClientLogoByTrackId): URL first, falling back to the sessionStorage
  // copy saved below, same fallback expiredLinkRedirect already uses.
  const [trackId, setTrackId] = useState<string | null>(null);

  useEffect(() => {
    // Sign in by magic link,
    // it may include the trackId if the link is generated for invitation
    const urlTrackId = getTrackIdFromUrl();
    if (urlTrackId) {
      saveTrackIdToStorage(urlTrackId);
    }
    setTrackId(urlTrackId ?? getTrackIdFromStorage());
  }, []);

  // Resolves the magic-link continue screen's requirements panel from the SAME trackId as the
  // attribution slot above, gated on the SAME toggle magicLinkPending itself is gated on below:
  // with the toggle off, magicLinkPending is undefined and nothing this hook resolves is ever
  // read, so the hook is fed null rather than trackId to make sure it makes no request either.
  // The hook already returns early (no fetch, cleared state) for a falsy argument, so this is
  // the argument to change, not an early return here, which would break the rules of hooks. No
  // trackId, an empty itemList, loading, an error, or a null identityDocumentCount all fall out
  // of this one call as metadata being undefined (or its count being null):
  // buildIdentityRequirementGroups already treats those identically, so there is nothing further
  // to branch on here. In particular, associationType is deliberately never inspected in this
  // page: the hook already gates on it, and the backend field's own description already encodes
  // the null-for-task-based rule.
  const { metadata } = useWorkflowMetadataByTrackingReference(
    isInertMagicLinkEnabled ? trackId : null,
  );
  const documentGroups = buildIdentityRequirementGroups(metadata?.identityDocumentCount);

  // Memoised so the identity stays stable across renders. PasswordlessLogin's
  // own effect depends on this callback, and a fresh function every render
  // (the previous behaviour) re-ran that effect on every render.
  const signInRedirect = useCallback(() => {
    routerRef.current.push({
      pathname: INITIALISE_SESSION,
      query: { ...routerRef.current.query },
    });
  }, []);

  // Memoised for the same reason as signInRedirect above.
  const expiredLinkRedirect = useCallback((reason?: RecoveryReason) => {
    // A used or expired link's second visit may not carry the trackId in
    // its query string, so fall back to the copy saved on first visit
    // (see the mount effect above).
    const trackId = getTrackIdFromUrl() ?? getTrackIdFromStorage();
    // No trackId means that it is not the sign-up flow and we
    // currently only focus on sign-up link expired,
    // thus keep in the login page if no trackId
    if (!trackId) {
      return;
    }

    const action = getActionFromUrl();
    // We need this action to recognise that the url is redirected from
    // the recover-invitation and ask to login
    if (action === 'login') {
      return;
    }

    // reason is passed straight through, including undefined: DECISION D1
    // reserves an absent reason to select the recovery page's default copy.
    routerRef.current.push(buildRecoveryUrl(trackId, reason));
  }, []);

  const getMagicLinkReriectUrl = async () => {
    const origin = location.origin;
    let trackId = getTrackIdFromUrl();
    if (!trackId) {
      trackId = getTrackIdFromStorage();
    }
    if (!trackId) {
      return origin;
    }

    return `${origin}?trackId=${trackId}`;
  };

  const isStepUp = getActionFromUrl() === 'login';

  return (
    <Landing
      title={LANDING_TITLE}
      description={LANDING_DESCRIPTION}
      logo={<ClarityLogo colorMode="onPrimary" />}
      mobileNav={isInertMagicLinkEnabled}
    >
      <PasswordlessLogin
        signInCallback={signInRedirect}
        getMegicLinkRedirectUri={getMagicLinkReriectUrl}
        onSignInAsDifferentUser={() => {
          clearLastAuthEmailFromUrl();
        }}
        expiredLinkCallback={expiredLinkRedirect}
        initialEmail={getLastAuthEmailFromUrl()}
        notice={
          isStepUp ? (
            <LoginAlert severity="info" fullwidth>
              For your security, please sign in again with your passkey or authenticator to
              continue.
            </LoginAlert>
          ) : undefined
        }
        magicLinkPending={
          isInertMagicLinkEnabled
            ? {
                title: 'Continue to VerifiMe',
                actionLabel: 'Continue',
                // attribution is explicitly set to undefined (never an
                // omitted key) when there is no trackId: a returning
                // customer's link carries none, and this records that as a
                // deliberate "no organisation" decision rather than an
                // oversight. client-portal's index.tsx makes the same
                // decision the same way, for a professional who never has
                // one to resolve.
                attribution: trackId ? <ClientLogoByTrackId trackId={trackId} /> : undefined,
                // buildIdentityRequirementGroups returns the "documents" group only (Ruling 9
                // deleted its mobile-hidden camera group entirely). The camera/webcam
                // requirement is desktop-only (C3) and is Task 16's own, separate
                // desktopRequirements prop, not this one. documentGroups itself is resolved once,
                // above, from the SAME trackId as attribution: requirements and
                // desktopRequirements both read it rather than each deriving their own.
                requirements: documentGroups ? (
                  <IdentityRequirementSummary
                    heading="What you will need"
                    groups={documentGroups}
                  />
                ) : undefined,
                // The desktop complement to `requirements` above: the SAME documents groups plus
                // a webcam requirement group, followed by the QR reveal for a customer whose
                // desktop has no camera. A second, independent key so it can never overwrite
                // `requirements`'s value.
                //
                // Both sides of this merge mattered here. #2206 wired documentGroups to the real
                // metadata?.identityDocumentCount (resolved once, above), replacing a local
                // `undefined /* backend field not yet shipped */` placeholder. #2210 moved the QR
                // reveal out of a sibling Stack and into the webcam group's own trailing slot,
                // because the board nests it inside that group's card (`.qr` is a child of
                // `.reqcard`). This keeps both: the real document count AND the nested slot.
                desktopRequirements: documentGroups ? (
                  <IdentityRequirementSummary
                    heading="What you will need"
                    groups={[
                      ...documentGroups,
                      {
                        ...buildDesktopCameraGroup(),
                        trailingSlot: linkUrl ? <DesktopQrReveal linkUrl={linkUrl} /> : undefined,
                      },
                    ]}
                  />
                ) : undefined,
                // The label above the countdown. Approved copy (slot 06, approved 2026-09-21) - the
                // time itself is never authored here: it is built downstream from the hook's own
                // pendingSignInLinkExpiresAt, decoded from the magic link's own JWT fragment
                // (packages/cognito-passwordless-auth/src/magic-link.ts's
                // checkCurrentLocationForSignInLink, confirmed to be the link's own `exp` claim,
                // not a session's). PasswordlessLogin (cognito-passwordless-auth) already omits the
                // whole clock whenever that value is unavailable, so supplying the label here is
                // safe regardless of whether a given link's fragment decodes.
                expiryLabel: 'Your link is valid until',
                errorMessage: "We couldn't sign you in. Try the button again.",
              }
            : undefined
        }
        // Task 5 ("Link sent, one screen", settled 2026-09-17): the sign-in route's resend
        // affordance is now LinkSentSignInResend, composed inside cognito-passwordless-auth's own
        // PasswordlessLogin container (not this page) once a send has actually happened - this
        // page's own job is just the copy bag that container forwards straight into it, the same
        // board slots every route's resend composition draws from.
        signInLinkRequested={
          isInertMagicLinkEnabled
            ? {
                leadLine: "Didn't get it?",
                resendActionLabel: 'Resend the email',
                pressedInsideWindowMessage: 'Please try again later, we will let you know.',
                replacementWarning: LINK_REPLACEMENT_WARNING,
                resendNowMessage: RESEND_NOW_MESSAGE,
                codePrompt: 'Enter the code from your authenticator app.',
                resendButtonLabel: 'Resend',
                codeRejectedMessage: 'Code not accepted.',
              }
            : undefined
        }
      >
        <SuccessIcon color="success" />
      </PasswordlessLogin>
    </Landing>
  );
}
